import React, { useEffect, useState } from 'react';

import {
  Button,
  Collapse,
  Form,
  Input,
  Space,
  Typography,
  Select,
  DatePicker,
  AutoComplete,
} from 'antd';
import { Store } from 'antd/es/form/interface';
import { CaretRightOutlined, PlusOutlined } from '@ant-design/icons';

import {
  VALIDATION_ERROR_MESSAGES,
  PLATFORM_PUBLICATION,
  TARGET,
  MR_PLATFORM,
  ENSEMBL_PLATFORM,
} from '../../../../constants';
import { MediaUploader } from '../../../../ui';
import TownsTags from '../../../../ui/Tags/TownsTags';
import DistrictsTags from '../../../../ui/Tags/DistrictsTags';

import { ApiPublicationType } from '../../types';
import { UploadedMediaType } from '../../../../ui/MediaUploader/types';

import styles from '../../Publications.module.css';
import locale from 'antd/lib/date-picker/locale/fr_FR';
import dayjs from 'dayjs';
import { getNow } from 'src/helpers/date';
import {
  usePlacesAutocomplete,
  PlaceSuggestion as Suggestion,
} from 'src/hooks/usePlacesAutocomplete';
import { connect } from 'react-redux';
import { RootState } from 'src/reducers/store';

type FormViewProps = {
  onSubmit: (rule: any) => void;
  loading: boolean;
  event?: ApiPublicationType;
  isMine?: boolean;
  allTowns: Array<{ id: string; name: string }>;
};

const { Panel } = Collapse;
const { Title } = Typography;
const { Option } = Select;

const disabledDate = (current: any) =>
  getNow() > getNow().hour(11).minute(0)
    ? current <= dayjs().endOf('day')
    : current < dayjs().startOf('day');

const requiredValidationRule = {
  required: true,
  message: VALIDATION_ERROR_MESSAGES.required,
};

const FormView: React.FC<FormViewProps> = ({
  onSubmit,
  loading,
  event,
  isMine = true,
  allTowns,
}) => {
  const [form] = Form.useForm();

  const {
    setValue,
    suggestions: { status, data },
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'FR' },
      types: ['address'],
    },
    debounce: 200,
  });

  const [places, setPlaces] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (status === 'OK') {
      setPlaces(data);
    } else {
      setPlaces([]);
    }
  }, [status, data]);

  const handleChange = (data: string) => {
    setValue(data);
  };

  const handleSelect = (address: string) => {
    const selectedPlace = places.find(
      (place: Suggestion) => place.description === address
    );
    if (selectedPlace) {
      setPlace(selectedPlace.place_id);
    }
  };

  const handleSearch = () => {
    // This is called by antd AutoComplete but we handle updates via useEffect
  };

  let initialValues;

  if (event) {
    initialValues = {
      title: event.title,
      message: event.text,
      location: event.event_place?.address.formatted_address,
      startDate: event.start_at ? dayjs(event?.start_at, 'YYYY-MM-DD') : '',
      endDate: event.end_at ? dayjs(event?.end_at, 'YYYY-MM-DD') : '',
      medias: event.medias.map(media => ({
        id: media.id,
        name: media.name,
        status: 'done',
        url: media.url.original,
      })),
      platform: event.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? MR_PLATFORM
        : ENSEMBL_PLATFORM,
      target: event.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? TARGET.national
        : TARGET.city,
      targetDistricts: event.target_districts || [],
      targetTowns: event?.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? []
        : event.target_towns.length
        ? event.target_towns.map(townName =>
            allTowns.find(({ name }) => {
              return name === townName;
            })
          )
        : event.diffusions.map(({ space }) =>
            allTowns.find(({ id }) => {
              return id === space.id;
            })
          ),
    };
  } else {
    initialValues = {
      title: '',
      message: '',
      location: null,
      startDate: '',
      endDate: '',
      medias: [],
      platform: null,
      target: null,
      targetDistricts: [],
      targetTowns: [],
    };
  }

  const [title] = useState(
    event ? 'Modifier mon évènement' : 'Créer un évènement'
  );
  const [disableInput] = useState(!isMine);
  const [platform, setPlatform] = useState(initialValues.platform);
  const [target, setTarget] = useState(initialValues.target);
  const [towns, setTowns] = useState(initialValues.targetTowns);
  const [startDate, setStartDate] = useState<any>(initialValues.startDate);
  const [place, setPlace] = useState<string>(
    event?.event_place?.place_id || ''
  );

  const disabledEndDate = (current: any) =>
    startDate && startDate.diff(current) > 0;

  const submitForm = async (values: Store) => {
    const dataToPost = {
      title: values.title,
      text: values.message,
      label: 'event',
      event_place: place,
      medias: values.medias.map((media: UploadedMediaType) => ({
        id: media.id,
      })),
      publisher: {
        id: process.env.REACT_APP_AUTOMATION_PUBLISHER_ID,
        model_type: 'user',
      },
      start_at: values.startDate,
      end_at: values.endDate,
    };

    if (values.platform === MR_PLATFORM) {
      dataToPost.target_platforms = PLATFORM_PUBLICATION[values.platform];
    }

    if (
      values.platform === ENSEMBL_PLATFORM &&
      values.target === TARGET.city &&
      towns.length === 1 &&
      values.targetDistricts.length
    ) {
      dataToPost.target_towns = towns.map(({ name }) => name);
      dataToPost.target_districts = values.targetDistricts;
      dataToPost.target_platforms = PLATFORM_PUBLICATION[values.platform];
    }

    if (
      values.platform === ENSEMBL_PLATFORM &&
      values.target === TARGET.city &&
      towns.length >= 1 &&
      (!values.targetDistricts || !values.targetDistricts?.length)
    ) {
      dataToPost.diffusions = towns.map(({ id }) => ({
        space: { id: id, model_type: 'place' },
      }));
    }

    onSubmit(dataToPost);
  };

  useEffect(() => {
    !loading && form.resetFields();
  }, [loading, form]);

  return (
    <Form
      id="eventForm"
      form={form}
      onFinish={submitForm}
      initialValues={initialValues}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Title level={3} style={{ marginTop: '5px' }}>
          {title}
        </Title>
        <Form.Item name="title" rules={[requiredValidationRule]}>
          <Input
            placeholder="Titre de l'évènement"
            id="title"
            disabled={disableInput}
          />
        </Form.Item>
        <Collapse
          bordered={false}
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Panel
            id="descriptionPanel"
            forceRender={true}
            header={
              <>
                <div className={styles.circle}>1</div>
                <span>Rédigez votre message</span>
              </>
            }
            key="message"
          >
            <Form.Item
              name="message"
              rules={[
                {
                  required: true,
                  message: VALIDATION_ERROR_MESSAGES.required,
                },
                {
                  max: 400,
                  message: VALIDATION_ERROR_MESSAGES.len,
                },
              ]}
            >
              <Input.TextArea
                id="message"
                placeholder="Message (maximum 400 caractères)"
                disabled={disableInput}
              />
            </Form.Item>
            <Form.Item
              name="location"
              rules={[
                {
                  required: true,
                  message: VALIDATION_ERROR_MESSAGES.required,
                },
              ]}
            >
              <AutoComplete
                id="location"
                allowClear
                defaultValue={event?.event_place?.address.formatted_address}
                style={{ width: '100%' }}
                size="large"
                onSearch={handleSearch}
                onChange={handleChange}
                onSelect={handleSelect}
                placeholder="Lieu de l'évènement"
                data-testid="autoCompletePlaces"
              >
                {places.length > 0 &&
                  places.map((place: Suggestion) => {
                    return (
                      <Option key={place.place_id} value={place.description}>
                        {place.description}
                      </Option>
                    );
                  })}
              </AutoComplete>
            </Form.Item>
            <Space size="middle" style={{ width: '100%' }}>
              <Space
                size="small"
                style={{ width: '100%', alignItems: 'baseline' }}
              >
                <Form.Item name="startDate" rules={[requiredValidationRule]}>
                  <DatePicker
                    id="startDate"
                    placeholder="Date de début"
                    format="DD/MM/YYYY"
                    locale={locale}
                    onChange={(value: any) => setStartDate(value)}
                    disabledDate={disabledDate}
                    disabled={disableInput}
                  />
                </Form.Item>
              </Space>
              <Space
                size="small"
                style={{ width: '100%', alignItems: 'baseline' }}
              >
                <Form.Item name="endDate">
                  <DatePicker
                    id="endDate"
                    placeholder="Date de fin"
                    format="DD/MM/YYYY"
                    locale={locale}
                    disabled={disableInput}
                    disabledDate={disabledEndDate}
                  />
                </Form.Item>
              </Space>
            </Space>
            <Form.Item key="medias" name="medias">
              <MediaUploader
                limit={1}
                placeholder="Télécharger des photos ou des documents"
                pictureListType="picture-card"
                defaultValue={initialValues.medias}
                disabled={disableInput}
                canRemove
                onUpload={(medias: Array<UploadedMediaType>) => {
                  form.setFieldsValue({
                    medias,
                  });
                }}
              >
                <Button id="media" icon={<PlusOutlined />}></Button>
              </MediaUploader>
            </Form.Item>
          </Panel>
          <Panel
            id="targetPanel"
            forceRender={true}
            header={
              <>
                <div className={styles.circle}>2</div>
                <span>Définissez votre ciblage</span>
              </>
            }
            key="target"
          >
            <Form.Item name="platform" rules={[requiredValidationRule]}>
              <Select
                id="platform"
                placeholder="Platforme"
                disabled={disableInput}
                onChange={(platform: string) => {
                  setPlatform(platform);
                  form.setFieldsValue({
                    target: null,
                  });
                  setTarget(null);
                }}
                style={{ width: '30%' }}
              >
                <Option id="ensembl" value={ENSEMBL_PLATFORM}>
                  Ensembl
                </Option>
                <Option id="maResidence" value={MR_PLATFORM}>
                  Ma-residence
                </Option>
              </Select>
            </Form.Item>
            <Form.Item name="target" rules={[requiredValidationRule]}>
              <Select
                id="target"
                placeholder="Ciblage"
                disabled={disableInput}
                onChange={(target: string) => {
                  form.setFieldsValue({
                    target,
                  });
                  setTarget(target);
                }}
                style={{ width: '210px' }}
              >
                {platform === MR_PLATFORM && (
                  <Option id="national" value={TARGET.national}>
                    Tous les utilisateurs MR
                  </Option>
                )}
                {platform === ENSEMBL_PLATFORM && (
                  <Option id="city" value={TARGET.city}>
                    Ville
                  </Option>
                )}
              </Select>
            </Form.Item>
            {target === TARGET.city && (
              <>
                <Form.Item name="targetTowns" rules={[requiredValidationRule]}>
                  <TownsTags
                    defaultValue={initialValues.targetTowns?.map(
                      ({ name }) => name
                    )}
                    disabled={disableInput}
                    onSelectChange={(targetTowns: Array<string>) => {
                      form.setFieldsValue({
                        targetTowns,
                      });
                      setTowns(targetTowns);
                    }}
                  />
                </Form.Item>
                {towns.length === 1 && (
                  <Form.Item name="targetDistricts">
                    <DistrictsTags
                      town={towns[0]?.name}
                      disabled={disableInput}
                      defaultValue={initialValues.targetDistricts}
                      onSelectChange={(targetDistricts: Array<string>) =>
                        form.setFieldsValue({
                          targetDistricts,
                        })
                      }
                    />
                  </Form.Item>
                )}
              </>
            )}
          </Panel>
        </Collapse>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            id="submit"
            htmlType="submit"
            type="primary"
            disabled={disableInput}
            loading={loading}
          >
            Publier
          </Button>
        </div>
      </Space>
    </Form>
  );
};

const mapStateToProps = ({ towns }: RootState) => ({ allTowns: towns });

export default connect(mapStateToProps)(FormView);
